import { type JSX,useCallback, useState } from 'react';

import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import { DeleteAclEntryThunkResponse } from 'common/services/api/Acls/AclEntitiesApiTypeDefs';
import { deleteAclEntry } from 'common/services/api/Acls/AclEntries.thunk';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { PrincipalType } from 'common/types/Entities';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { getAssociatedLicense } from 'common/utils/getAssociatedLicenseHelpers';
import { selectFilters } from 'features/licenseAclEntries/licenseAclEntriesSlice';
import { selectLicensesByTenantId } from 'features/licenses/licensesSlice';
import { updateUserProfileTenant,UserProfileTenantUpdatePayload } from 'features/userProfileApps/helpers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import {
    selectCurrentEntityType,
    selectDeleteCurrentTenantId,
    setCurrentEntityType,
    setDialogOpen
} from '../../common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, selectPaginationGridData, setPaginationGridData, setRows } from '../../common/components/PalmDataGrid/palmDataGridSlice';
import { DeleteDialogContentProps, DrawerEntitiesNames } from '../../common/types/TypeDefs';

import { AclEntiriesDeleteDialogProperties, AclEntriesPage,AclInfo } from './aclEntriesTypeDefs';

export function GetDeleteDialogContent(dialogProps: AclEntiriesDeleteDialogProperties) {
    return AclEntriesDeleteDialogContent.bind(null, dialogProps);
}

export function AclEntriesDeleteDialogContent(
    dialogProps: AclEntiriesDeleteDialogProperties,
    itemProps: DeleteDialogContentProps<AclInfo>) : JSX.Element  {

    const editingRow = itemProps.editingRow;
    const itemId = itemProps.itemId;
    const tenantId = editingRow?.tenantId ?? itemProps.tenant?.id ?? '';
    const { tenantRequiresLicense } = dialogProps;

    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const pageInfo = useAppSelector(selectPageInfo);
    const licenses = useAppSelector(selectLicensesByTenantId(editingRow?.tenantId ?? ''));
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const filterProp = useAppSelector(selectFilters);

    const { rowsOnPage } = itemProps?.paginationInfo ?? {};
    const { currentPage = 0, rowsPerPage = 0, sortModel } = paginationGridData ?? {};
    const { keywords } = filterProp;

    let principalType = getPrincipalType(dialogProps.aclPage);
    let usergroupId = dialogProps.aclPage == AclEntriesPage.TenantUserGroup ? dialogProps.principalId : undefined;
    let userId = dialogProps.aclPage == AclEntriesPage.TenantUserGroup ? undefined : dialogProps.principalId;
    let principalName = dialogProps?.principalName;
    let billingRecord = dialogProps?.licenseBillingRecord;

    if(dialogProps.aclPage == AclEntriesPage.License) {
        principalType = PrincipalType.User;
        if (!itemProps.editingRow?.userId) {
            principalType = PrincipalType.UserGroup;
        }

        principalName = itemProps.editingRow?.principalName ?? '';
        usergroupId = itemProps.editingRow?.usergroupId;
        userId = itemProps.editingRow?.userId;
    }
    else {
        const associatedLicense = getAssociatedLicense(editingRow, licenses);
        billingRecord = associatedLicense?.billingRecord;
    }

    const commonSuccessHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('ACL Entry has been deleted.', { variant: 'success' });
    };

    const _getBaseAclItemDeleteParams = () => {
        const deleteParams = {
            aclItem: {
                tenantId: tenantId,
                usergroupId: usergroupId,
                userId: userId,
                accessId: editingRow?.accessId,
                resourceId: editingRow?.resourceId,
                licenseId: editingRow?.licenseId,
                tenantRequiresLicense,
            },
            paginationInfo: {}
        };
        return deleteParams;
    };

    const _getPaginatedAclItemDeleteParams = () => {
        const skip = (rowsOnPage === 1) && (currentPage > 0) ?
            ((currentPage - 1) * rowsPerPage) :
            (currentPage * rowsPerPage);

        const deleteParams = {
            aclItem: {
                tenantId: tenantId,
                usergroupId: usergroupId,
                userId: userId,
                accessId: editingRow?.accessId,
                resourceId: editingRow?.resourceId,
                licenseId: editingRow?.licenseId,
            },
            paginationInfo: {
                skip: skip,
                take: rowsPerPage,
                search: keywords[0],
                sortKey: sortModel?.[0]?.field,
                sortOrder: sortModel?.[0]?.sort,
            }
        };
        return deleteParams;
    };

    const _getDeleteParamsByPage = () => {
        if(dialogProps.aclPage === AclEntriesPage.License) {
            return _getPaginatedAclItemDeleteParams();
        } else {
            return _getBaseAclItemDeleteParams();
        }
    };

    const  _getUserProfileTenantUpdatePayload = (
        dialogProps: AclEntiriesDeleteDialogProperties,
        tenantId: string,
        userId: string
    ): UserProfileTenantUpdatePayload => ({
        tenantId,
        userId,
        currentUserProfileTenant: dialogProps?.currentUserProfileTenant
    });

    const _processDeleteUserProfileAclEntries = (): void => {
        dispatch(setCurrentEntityType());
        const payload = _getUserProfileTenantUpdatePayload(dialogProps, tenantId, userId ?? '');
        updateUserProfileTenant(payload);
    };

    const _processDeleteLicenseAclEntries = (response: DeleteAclEntryThunkResponse): void => {
        dispatch(setPaginationGridData({
            rows: response.rows,
            rowsCount: response.count,
            currentPage: response.skipped ? (response.skipped / rowsPerPage) : 0,
            rowsPerPage,
            sortModel,
        }));
    };

    const _processDeleteDefaultAclEntries = (response: DeleteAclEntryThunkResponse): void => {
        dispatch(setRows?.(response.rows));
    };

    const AclEntitiesPageSuccessHandlersMapper: Record<AclEntriesPage, (response: DeleteAclEntryThunkResponse) => void> = {
        [AclEntriesPage.UserProfile]: _processDeleteUserProfileAclEntries,
        [AclEntriesPage.License]: _processDeleteLicenseAclEntries,
        [AclEntriesPage.TenantUser]: _processDeleteDefaultAclEntries,
        [AclEntriesPage.TenantUserGroup]: _processDeleteDefaultAclEntries
    };

    const handleAclEntityDeleteSuccess = (response: DeleteAclEntryThunkResponse) => {
        commonSuccessHandler();
        const handleSuccessInCurrentPage = AclEntitiesPageSuccessHandlersMapper[dialogProps.aclPage];
        handleSuccessInCurrentPage(response);
    };

    const handleAclEntriesDeleteError = (error: { message: string; }) => {
        const errorStatusCode = extractStatusCode(error.message);
        if (errorStatusCode === 404) {
            commonSuccessHandler();
            dispatch(setPaginationGridData({}));
            return;
        }
        handleDeleteDialogError('ACL Entry', enqueueSnackbar, errorStatusCode);
    };

    const aclResourceDeleteCallback = useCallback(() => {
        if (!itemId) return;

        const deleteParams = _getDeleteParamsByPage();

        setProcessing(true);
        dispatch(deleteAclEntry({
            item: deleteParams.aclItem,
            aclPage: dialogProps.aclPage,
            paginationInfo: deleteParams.paginationInfo
        }))
            .unwrap()
            .then(handleAclEntityDeleteSuccess)
            .catch(handleAclEntriesDeleteError)
            .finally(() => {
                setProcessing(false);
            });
    }, [itemId]);

    const currentEntityType = useAppSelector(selectCurrentEntityType);
    const currentTenantId = useAppSelector(selectDeleteCurrentTenantId);

    if (
        dialogProps.aclPage == AclEntriesPage.UserProfile &&
        (
            currentEntityType !== DrawerEntitiesNames.UserAcls ||
            currentTenantId !== tenantId
        )
    ) {
        return <></>;
    }

    return  (
        <DeleteDialog deleteCallback={aclResourceDeleteCallback} processing={processing ?? false} title='ACL Entry'>
            <DeleteDialogCustomField fieldName="ACL Resource" fieldValue={editingRow?.resourceTableName ?? editingRow?.resourceName} />
            <DeleteDialogCustomField fieldName="ACL Access" fieldValue={editingRow?.accessTableName ?? editingRow?.accessName} />
            {billingRecord &&
                <DeleteDialogCustomField fieldName="Billing Record" fieldValue={billingRecord} />
            }
            <DeleteDialogCustomField
                fieldName={principalType == PrincipalType.User ? 'User Name': 'User Group Name'}
                fieldValue={principalName} />
            <DeleteDialogCustomField
                fieldName="Tenant Name"
                fieldValue={pageInfo?.tenantName ?? pageInfo?.name ?? itemProps?.tenant?.name} />
        </DeleteDialog>
    );
}

function getPrincipalType(aclPage: AclEntriesPage) {
    return aclPage == AclEntriesPage.TenantUserGroup
        ? PrincipalType.UserGroup
        : PrincipalType.User;
}
